"use client";

import { useEffect, useMemo, useState } from "react";
import Link from "next/link";

type GroupType = "sector" | "industry";
type DualItem = {
  name: string;
  constituentCount: number;
  capWeightIndex: number | null;
  equalWeightIndex: number | null;
  capRsRatio: number | null;
  capRsMomentum: number | null;
  capQuadrant: string | null;
  equalRsRatio: number | null;
  equalRsMomentum: number | null;
  equalQuadrant: string | null;
  divergenceLabel: string | null;
  divergenceScore: number | null;
  daysInDivergence: number | null;
};
type Response = { latestDate: string | null; items: DualItem[] };
const quadBadge: Record<string, string> = {
  Leading: "border-up/30 bg-up/10 text-up", Improving: "border-sky-400/30 bg-sky-400/10 text-sky-300",
  Weakening: "border-amber-300/30 bg-amber-300/10 text-amber-300", Lagging: "border-down/30 bg-down/10 text-down",
};
const divergenceBadge: Record<string, string> = {
  stealth_accumulation: "border-up/30 bg-up/10 text-up",
  confirmed_trend: "border-emerald-400/30 bg-emerald-400/10 text-emerald-300",
  narrow_leadership: "border-amber-300/30 bg-amber-300/10 text-amber-300",
  unconfirmed_bounce: "border-border bg-surface-2 text-muted",
};
const DUAL_STATUS_ORDER: Partial<Record<keyof DualItem, (string | null)[]>> = {
  capQuadrant: ["Leading", "Improving", "Weakening", "Lagging", null],
  equalQuadrant: ["Leading", "Improving", "Weakening", "Lagging", null],
  divergenceLabel: ["stealth_accumulation", "confirmed_trend", "narrow_leadership", "unconfirmed_bounce", null],
};
const cell = (value: number | null, digits = 2) => value == null || !Number.isFinite(value) ? "—" : value.toLocaleString("en-US", { minimumFractionDigits: digits, maximumFractionDigits: digits });

export function DualRrgPage() {
  const [groupType, setGroupType] = useState<GroupType>("sector");
  const [data, setData] = useState<Response | null>(null);
  const [error, setError] = useState(false);
  const [sort, setSort] = useState<{ key: keyof DualItem; direction: "asc" | "desc"; statusIndex?: number }>({ key: "name", direction: "asc" });
  useEffect(() => {
    let cancelled = false;
    fetch(`/api/analytics/dual-rrg?type=${groupType}`, { cache: "no-store" })
      .then((response) => { if (!response.ok) throw new Error(); return response.json() as Promise<Response>; })
      .then((result) => { if (!cancelled) setData(result); })
      .catch(() => { if (!cancelled) setError(true); });
    return () => { cancelled = true; };
  }, [groupType]);

  const columns: { key: keyof DualItem; label: string; numeric?: boolean }[] = [
    { key: "name", label: groupType === "sector" ? "Sector" : "Industry" },
    { key: "capQuadrant", label: "Cap Quadrant" },
    { key: "equalQuadrant", label: "Equal Quadrant" },
    { key: "divergenceLabel", label: "Divergence" },
    { key: "capWeightIndex", label: "Cap Index", numeric: true },
    { key: "equalWeightIndex", label: "Equal Index", numeric: true },
    { key: "capRsRatio", label: "Cap RS-Ratio", numeric: true },
    { key: "capRsMomentum", label: "Cap RS Momentum", numeric: true },
    { key: "equalRsRatio", label: "Equal RS-Ratio", numeric: true },
    { key: "equalRsMomentum", label: "Equal RS Momentum", numeric: true },
    { key: "divergenceScore", label: "Divergence Score", numeric: true },
    { key: "daysInDivergence", label: "Days in Divergence", numeric: true },
  ];
  const items = useMemo(() => [...(data?.items ?? [])].sort((left, right) => {
    const statusOrder = DUAL_STATUS_ORDER[sort.key];
    if (statusOrder && sort.statusIndex != null) {
      const rank = (item: DualItem) => {
        const value = item[sort.key];
        const index = statusOrder.indexOf(typeof value === "string" ? value : null);
        return (index - sort.statusIndex! + statusOrder.length) % statusOrder.length;
      };
      return rank(left) - rank(right);
    }
    const a = left[sort.key], b = right[sort.key];
    if (a == null) return b == null ? 0 : 1;
    if (b == null) return -1;
    const order = typeof a === "number" && typeof b === "number" ? a - b : String(a).localeCompare(String(b));
    return sort.direction === "asc" ? order : -order;
  }), [data, sort]);
  const toggleSort = (key: keyof DualItem) => setSort((current) => {
    const statusOrder = DUAL_STATUS_ORDER[key];
    if (statusOrder) return { key, direction: "asc", statusIndex: current.key === key && current.statusIndex != null ? (current.statusIndex + 1) % statusOrder.length : 0 };
    return { key, direction: current.key === key && current.direction === "asc" ? "desc" : "asc" };
  });

  return <div className="mx-auto flex w-full max-w-[1440px] flex-col gap-4">
    <header className="flex flex-wrap items-center justify-between gap-3">
      <h1 className="text-xl font-semibold tracking-tight">Dual RRG</h1>
      <div className="inline-flex rounded-xl border border-border bg-surface p-1" aria-label="Dual RRG universe">
        {(["sector", "industry"] as const).map((value) => <button key={value} type="button" onClick={() => { if (value !== groupType) { setData(null); setError(false); setGroupType(value); } }} aria-pressed={groupType === value} className={`rounded-lg px-4 py-2 text-sm ${groupType === value ? "bg-accent/20 text-accent" : "text-muted hover:text-foreground"}`}>{value === "sector" ? "Sector" : "Industry"}</button>)}
      </div>
    </header>
    <section className="overflow-hidden rounded-2xl border border-border bg-surface">
      <div className="flex items-center justify-between border-b border-border px-5 py-4"><h2 className="font-semibold">{groupType === "sector" ? "Sector" : "Industry"} Dual Rotation</h2><span className="text-xs text-muted">Daily · {data?.latestDate ?? "—"}</span></div>
      <div className="max-h-[calc(100vh-230px)] min-h-52 overflow-auto">
        {error ? <div role="status" className="p-8 text-center text-sm text-down">Unable to load Dual RRG data.</div> : !data ? <DualTableSkeleton /> : <table className="w-full min-w-[1450px] text-sm">
          <thead className="sticky top-0 z-10 bg-surface text-left text-xs text-muted"><tr>{columns.map((column) => { const statuses = DUAL_STATUS_ORDER[column.key]; const active = sort.key === column.key; const statusIndex = active ? sort.statusIndex : undefined; const status = statuses && statusIndex != null ? statuses[statusIndex] : undefined; const statusLabel = status == null ? column.key === "divergenceLabel" ? "No divergence" : "No value" : status.replaceAll("_", " "); return <th key={column.key} aria-sort={active && statuses && statusIndex != null ? "other" : active ? sort.direction === "asc" ? "ascending" : "descending" : "none"} className={`whitespace-nowrap border-b border-border px-4 py-3 ${column.numeric ? "text-right" : ""}`}><button type="button" onClick={() => toggleSort(column.key)} className="inline-flex items-center gap-1.5 hover:text-foreground" aria-label={statuses ? `Cycle ${column.label} status${active && statusIndex != null ? `, current ${statusLabel}` : ""}` : `Sort by ${column.label}`}>{column.label}{active && statuses && statusIndex != null ? <span className="rounded bg-accent/10 px-1 text-[9px] font-normal text-accent">{statusLabel}</span> : <span className="text-[10px] text-accent">{active ? sort.direction === "asc" ? "↑" : "↓" : "↕"}</span>}</button></th>; })}</tr></thead>
          <tbody>{items.map((item) => <tr key={item.name} className="border-b border-border/60 hover:bg-white/[0.03]">{columns.map((column) => {
            const value = item[column.key];
            const quadrant = column.key === "capQuadrant" || column.key === "equalQuadrant";
            const badge = quadrant ? (typeof value === "string" ? quadBadge[value] : "") : column.key === "divergenceLabel" && typeof value === "string" ? divergenceBadge[value] ?? "border-border bg-surface-2 text-muted" : "";
            return <td key={column.key} className={`whitespace-nowrap px-4 py-3 ${column.numeric ? "text-right font-mono tabular-nums" : ""} ${column.key === "name" ? "font-medium text-foreground" : "text-muted"}`}>
              {column.key === "name" ? <Link href={`/rotation?type=${groupType}&name=${encodeURIComponent(item.name)}&from=dual-rrg`} className="font-medium text-accent underline decoration-dotted underline-offset-4 hover:text-foreground">{item.name} ↗</Link> : badge ? <span className={`inline-flex rounded-md border px-2 py-1 text-xs ${badge}`}>{typeof value === "string" ? value.replaceAll("_", " ") : value}</span> : column.key === "capWeightIndex" || column.key === "equalWeightIndex" || column.key === "divergenceScore" ? cell(value as number | null, 4) : typeof value === "number" ? cell(value, 2) : value ?? "—"}
            </td>;
          })}</tr>)}{items.length === 0 && <tr><td colSpan={columns.length} className="p-8 text-center text-sm text-muted">No Dual RRG data available.</td></tr>}</tbody>
        </table>}
      </div>
    </section>
  </div>;
}

function DualTableSkeleton() {
  return <div aria-label="Loading Dual RRG table" className="min-w-[1450px] animate-pulse p-4">
    <div className="mb-3 grid grid-cols-12 gap-3 border-b border-border pb-3">{Array.from({ length: 12 }, (_, i) => <div key={i} className="h-3 rounded bg-surface-2" />)}</div>
    {Array.from({ length: 12 }, (_, row) => <div key={row} className="grid grid-cols-12 gap-3 border-b border-border/50 py-3">{Array.from({ length: 12 }, (_, col) => <div key={col} className="h-4 rounded bg-surface-2" />)}</div>)}
  </div>;
}
